import { toHex, transparentize } from 'color2k';

import type { ThemedValue } from '@creditornot/cb-utils';

import { blendColorWithBackground } from './blendColorWithBackground';
import { readThemedValue } from './readThemedValue';

type ColorModifyFn = (color: string) => string;
type ModifierValue = string | ThemedValue;
type Modifier = (value: ModifierValue) => ModifierValue;

/**
 * Creates a modifier function that can be used to modify a color value based on the theme.
 * The modifier can be a function that takes a color and returns a modified color,
 * or an object with `dark`, `light`, and optionally `lightHighContrast` properties.
 */
export const createModifier =
  (
    modifier:
      | ColorModifyFn
      | {
          dark: ColorModifyFn;
          light: ColorModifyFn;
          lightHighContrast?: ColorModifyFn;
        },
  ): Modifier =>
  (value) => {
    const lightModifier = typeof modifier === 'function' ? modifier : modifier.light;
    const lightHighContrastModifier =
      typeof modifier === 'function' ? modifier : modifier.lightHighContrast;
    const darkModifier = typeof modifier === 'function' ? modifier : modifier.dark;

    const light = lightModifier(readThemedValue(value, 'light'));
    const lightHighContrast = lightHighContrastModifier
      ? lightHighContrastModifier(readThemedValue(value, 'lightHighContrast'))
      : undefined;
    const dark = darkModifier(readThemedValue(value, 'dark'));
    const darkElevated =
      typeof value === 'object' && typeof value.dark === 'object'
        ? darkModifier(value.dark.elevated)
        : undefined;

    return {
      light,
      lightHighContrast:
        lightHighContrast !== undefined && lightHighContrast !== light
          ? lightHighContrast
          : undefined,
      dark: darkElevated ? { default: dark, elevated: darkElevated } : dark,
    };
  };

export const pipeModifiers =
  (...modifiers: Modifier[]) =>
  (value: string | ThemedValue) =>
    modifiers.reduce((acc, modifier) => modifier(acc), value);

const trimResult = (result: ThemedValue) => {
  const trimmedResult = { ...result };

  if (
    trimmedResult.light === trimmedResult.lightHighContrast ||
    trimmedResult.lightHighContrast === undefined
  ) {
    delete trimmedResult.lightHighContrast;
  }

  if (
    typeof trimmedResult.dark === 'object' &&
    trimmedResult.dark.default === trimmedResult.dark.elevated
  ) {
    trimmedResult.dark = trimmedResult.dark.default;
  }

  return trimmedResult;
};

export const modifiers = {
  alpha: (alpha: { dark: number; light: number }) => (value: ModifierValue) => {
    const light = transparentize(readThemedValue(value, 'light'), 1 - alpha.light);
    const lightHighContrast = transparentize(
      readThemedValue(value, 'lightHighContrast'),
      1 - alpha.light,
    );
    const dark = transparentize(readThemedValue(value, 'dark'), 1 - alpha.dark);
    const darkElevated =
      typeof value === 'object' && typeof value.dark === 'object'
        ? transparentize(readThemedValue(value, 'dark', true), 1 - alpha.dark)
        : undefined;

    return trimResult({
      light,
      lightHighContrast,
      dark: darkElevated ? { default: dark, elevated: darkElevated } : dark,
    });
  },
  solidify: (value: ModifierValue) => {
    const light = blendColorWithBackground('#ffffff', readThemedValue(value, 'light'));
    const lightHighContrast = blendColorWithBackground(
      '#ffffff',
      readThemedValue(value, 'lightHighContrast'),
    );
    const dark = blendColorWithBackground('#000', readThemedValue(value, 'dark'));
    const darkElevated =
      typeof value === 'object' && typeof value.dark === 'object'
        ? blendColorWithBackground('#242424', readThemedValue(value, 'dark', true))
        : undefined;

    return trimResult({
      light,
      lightHighContrast,
      dark: darkElevated ? { default: dark, elevated: darkElevated } : dark,
    });
  },
  elevateOnDarkMode: (value: ModifierValue) => ({
    ...(typeof value === 'object' && value),
    light: readThemedValue(value, 'light'),
    dark: {
      default: readThemedValue(value, 'dark'),
      elevated: blendColorWithBackground('#242424', readThemedValue(value, 'dark', true)),
    },
  }),
  toHex: (value: ModifierValue) => createModifier(toHex)(value),
} satisfies Record<string, ((...args: any[]) => Modifier) | Modifier>;
